import classRoomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import "./Classroom.css";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import "./CreateClassroom.css";
import StudentSelector from "./StudentSelector";
import CustomInput from "../../../common/CustomInput";
import { useCreateClassroom } from "../../../../hooks/teacher/classroom/useCreateClassroom";
import { useSetQuizLimitClassroom } from "../../../../hooks/quiz/useSetQuizLimitClassroom";
import { useAssignStudents } from "../../../../hooks/teacher/classroom/useAssignStudents";
import { useTenantAuth } from "../../../../context/TeanantAuthProvider";
import { useRef, useState, useEffect, useMemo } from "react";
import BackButton from "../../TeacherAdmin/Goback/GoBack";
import { Button } from "react-bootstrap";
import SuccessModal from "../../../common/SuccessModal";
import { useGetQuizLimitStatus } from "../../../../hooks/quiz/useGetQuizLimitStatus";
import ScrollToError from "../../../../utils/ScrollToError";

export const classroomValidationSchema = Yup.object().shape({
  classroomName: Yup.string()
    .trim()
    .required("Classroom name is required")
    .min(3, "Classroom name must be at least 3 characters long")
    .max(100, "Classroom name must be at most 100 characters long")
    .matches(
      /^[A-Za-z0-9 _-]+$/,
      "Classroom name can only contain letters, numbers, spaces, underscores, and hyphens"
    ),
  students: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        student_code: Yup.string(),
      }),
    )
    .min(1, "Please select at least one student"),
});

export const quizLimitValidationSchema = Yup.object().shape({
  quizLimit: Yup.number()
    .typeError("Quiz limit must be a number")
    .required("Quiz limit is required")
    .min(1, "Quiz limit must be at least 1")
    .max(10, "Quiz limit must be at most 10")
    .integer("Quiz limit must be a whole number"),
});

const CreateClassroom = () => {
  const { loading, error, createClassroom } = useCreateClassroom();
  const { loading: assignLoading, assignStudents } = useAssignStudents();
  const { currentUser } = useTenantAuth();
  const { loading: quizLimitSetLoading, setQuizLimitClassroom } = useSetQuizLimitClassroom();
  const topRef = useRef(null);
  const classroomNameRef = useRef(null);
  const quizLimitRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [refreshStudents, setRefreshStudents] = useState(false);

  const { statusData: quizLimitStatus, loading: quizLimitLoading, getQuizLimitStatus } = useGetQuizLimitStatus();
  useEffect(() => {
    getQuizLimitStatus();
  }, []);

  const showQuizLimit = !quizLimitLoading && quizLimitStatus?.teacher_decides === true;

  const validationSchema = useMemo(() => {
    if (showQuizLimit) {
      return classroomValidationSchema.concat(quizLimitValidationSchema);
    }
    return classroomValidationSchema;
  }, [showQuizLimit]);
  /**
   * Success message state
   * @typedef {Object} Message
   * @property {string} message - Success message
   * @property {string} description - Success description
   * @property {"success"|"error"} type - Success type
   */
  const [successMessage, setSuccessMessage] = useState(
    /** @type {Message} */({
      message: "",
      description: "",
      type: "success",
    }),
  );

  const initialValues = {
    classroomName: "",
    students: [],
    quizLimit: "",
  };

  /**
   * Handles classroom creation and student assignment
   * @param {Object} values - Form values
   * @param {Object} formikHelpers - Formik helper methods
   */
  const handleCreateClassroom = async (values, { resetForm }) => {
    setSuccessMessage({
      message: "",
      description: "",
      type: "success",
    });

    // Step 1: Create the classroom
    const response = await createClassroom({
      teacher_id: currentUser.id || "",
      name: values.classroomName,
    });

    // If classroom creation failed, stop here
    if (!response) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
      return;
    }

    // Step 2: Assign students to the classroom
    const classroomId = response.data.id;
    const studentsToAssign = values.students.map((student) => {
      return {
        id: student.id,
        is_assign: true,
      };
    });

    const assignResponse = await assignStudents({
      classroomId,
      studentsData: studentsToAssign,
    });

    // Handle student assignment result
    if (!assignResponse) {
      // Classroom created but student assignment failed
      setSuccessMessage({
        message: "Classroom created successfully!",
        description:
          "Classroom created successfully, but there was an error while assigning the students to the class. Please try again by updating the classroom.",
        type: "error",
      });
      setShowModal(true);
      topRef.current.scrollIntoView({ behavior: "smooth" });
      return;
    }

    // Step 3: Set quiz limit if applicable
    if (showQuizLimit && values.quizLimit) {
      const quizLimitResponse = await setQuizLimitClassroom({
        classroom_id: classroomId,
        limit: Number(values.quizLimit),
      }, (success) => {

        if (!success) {
          setSuccessMessage({
            message: "Classroom created successfully!",
            description:
              "Classroom created and students assigned, but there was an error setting the quiz limit. Please update the classroom to set the quiz limit.",
            type: "error",
          });
          setShowModal(true);
          resetForm();
          setRefreshStudents((prev) => !prev);
          topRef.current.scrollIntoView({ behavior: "smooth" });
          return;
        }
      });
    }

    // Everything succeeded
    setSuccessMessage({
      message: "Classroom created successfully!",
      description:
        "Classroom has been created successfully and students have been assigned to the classroom.",
      type: "success",
    });
    setShowModal(true);
    resetForm();
    setRefreshStudents((prev) => !prev);
    topRef.current.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      <div className="">
        <BackButton />
      </div>
      <div className="classroom-cnt">
        <div className="classroom-container" ref={topRef}>
          <img src={classRoomImg} className="img" alt="Classroom" />
          <h2 className="text">Create Classroom</h2>
        </div>
        <div>
          {/* Success Message */}
          {/* {successMessage && (
            <div className="alert alert-success mt-3" role="alert">
              <i className="bi bi-check-circle-fill me-2"></i>
              {successMessage}
            </div>
          )}

          {/* Classroom Creation Error */}
          {error && (
            <div className="alert alert-danger mt-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}
        </div>

        <div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleCreateClassroom}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form className="">
                <ScrollToError
                  fieldRefs={{
                    classroomName: classroomNameRef,
                    quizLimit: quizLimitRef
                  }}
                />
                <div className="mt-0 input-container">
                  <CustomInput
                    type="text"
                    name="classroomName"
                    label="Classroom Name"
                    placeholder="Enter classroom name"
                    autoComplete="off"
                    layout="inline"
                    ref={classroomNameRef}
                  />
                </div>

                {quizLimitLoading ? (
                  <div className="mt-3 input-container">
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div
                        className="skeleton-box"
                        style={{ width: "120px", height: "20px", borderRadius: "4px" }}
                      />
                      <div
                        className="skeleton-box"
                        style={{ width: "100%", height: "38px", borderRadius: "6px" }}
                      />
                    </div>
                  </div>
                ) : showQuizLimit ? (
                  <div className="mt-3 input-container">
                    <CustomInput
                      type="number"
                      name="quizLimit"
                      label="Quiz Attempt Limit"
                      placeholder="Enter quiz attempt limit (1-10)"
                      autoComplete="off"
                      layout="inline"
                      min="1"
                      max="10"
                      ref={quizLimitRef}
                    />
                  </div>
                ) : null}

                <div className="mt-4">
                  <div className="add-student-classroom">Add Students</div>
                </div>

                <div className="mt-4">
                  <StudentSelector name="students" refresh={refreshStudents} />
                </div>

                <Button
                  type="submit"
                  className="custom-primary-button mt-4"
                  disabled={loading || assignLoading || quizLimitSetLoading}
                >
                  {loading || assignLoading || quizLimitSetLoading
                    ? "Creating Classroom..."
                    : "Save Classroom"}
                </Button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <SuccessModal
        type={successMessage.type}
        showModal={showModal}
        handleClose={setShowModal}
        title={successMessage.message}
        message={successMessage.description}
        primaryButtonText="Add Another"
        primaryButtonLink="/teacher/classroom/create"
        secondaryButtonText="Classrooms List"
        secondaryButtonLink="/teacher/classroom"
        handlePrimaryClickCallback={() => {
          setRefreshStudents((prev) => !prev);
        }}
      />
    </div>
  );
};

export default CreateClassroom;
